import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
// import { Dislike, Like } from '@saleshandy/icons';
import StepWrapper from '../../../../../shared/stepwrapper';
import AiSequenceModalFooter from '../ai-sequence-modal-footer/ai-sequence-modal-footer';
import { Images } from '../../../../../../../shared/app-constants';
import {
  EmailContentSizeAllowed,
  executeOnRequestStatus,
  getIsRequestPending,
  SubjectSizeAllowed,
} from '../../../../../../../shared/utils';
import EmailAccordionItem from '../../../../../shared/email-accordion-item/emailaccordionitem';
import { SequenceEmailContent } from '../../../type';
import { RequestStatus } from '../../../../../../../shared/enums/request-status';
import { stripChipTagsFromHtml } from '../../../../../../../shared/editor/helpers';
import { convertHtmlToText } from '../../../../../shared/modals/email-modal/components/sidebar/tabs/utils/get-parsed-content-for-text-only-email';

interface SequenceEditStepProps {
  sequenceList: {
    emails?: SequenceEmailContent[];
    name?: string;
  };
  handleRegenerateSequenceContent: (
    payload: SequenceEmailContent & { userPrompt: string },
  ) => void;
  handleUpdateAISequenceEmails: (steps: any[]) => void;
  regeneratedStep?: SequenceEmailContent;
  saveCurrentStepDetails?: (payload: any) => void;
  setRegeneratedStepValue: (payload: SequenceEmailContent) => void;
  saveAiSeqGeneratorStepDetailsRequestStatus: RequestStatus;
}

const SequenceEditStep: React.FC<SequenceEditStepProps> = ({
  sequenceList = {
    emails: [],
    name: '',
  },
  handleRegenerateSequenceContent = () => {},
  handleUpdateAISequenceEmails = () => {},
  regeneratedStep,
  saveCurrentStepDetails,
  setRegeneratedStepValue = () => {},
  saveAiSeqGeneratorStepDetailsRequestStatus,
}) => {
  const [isThreadMode, setIsThreadMode] = useState(true);
  const [steps, setSteps] = useState([]);
  const [expandedId, setExpandedId] = useState<number | null>(1);
  const [promptErrorStepId, setPromptErrorStepId] = useState<number | null>(
    null,
  );

  const [apiRef, setApiRef] = useState<boolean>(false);

  const updateStep = useCallback(
    (
      id: number,
      key: 'subject' | 'body' | 'aiContent' | 'isLoading',
      value: string | boolean,
    ) => {
      setSteps((prev) =>
        prev?.map((step) =>
          step?.id === id ? { ...step, [key]: value } : step,
        ),
      );
    },
    [],
  );

  const updateStepContent = useCallback((id: number, payload: any) => {
    setSteps((prev) =>
      prev?.map((step) => (step?.id === id ? { ...step, ...payload } : step)),
    );
  }, []);

  const isAnyStepInvalid = useMemo(
    () =>
      steps?.some((step) => {
        const subjectTooLong = step?.subject?.length > SubjectSizeAllowed;
        const bodyTooLong =
          new Blob([step?.body ?? ''])?.size > EmailContentSizeAllowed;
        const hasPromptError = promptErrorStepId === step?.id;
        const isSubjectEmpty = !step?.subject?.trim();
        const isBodyEmpty = !step?.body?.trim();

        return (
          subjectTooLong ||
          bodyTooLong ||
          hasPromptError ||
          isSubjectEmpty ||
          isBodyEmpty
        );
      }),
    [steps, promptErrorStepId],
  );

  const handleExpand = useCallback((id: number) => {
    setExpandedId((prev) => (prev === id ? null : id));
  }, []);

  const handleTriggerAI = useCallback(
    (stepId: number, aiContent: string) => {
      // ** Setting user input emails to preserve them while regenerating it
      handleUpdateAISequenceEmails(
        steps?.map((step) => ({
          subject: step?.subject || '',
          content: step?.body || '',
          userPrompt: aiContent,
          day: step?.day || '',
          order: step?.order || 1,
          remainingRegenrationQuota: step?.remainingRegenrationQuota || 0,
        })),
      );

      const actionStep = steps?.find((step) => step?.id === stepId);
      const payload = {
        subject: actionStep?.subject || '',
        content: actionStep?.body || '',
        userPrompt: aiContent,
        day: actionStep?.day || '',
        order: actionStep?.order || 1,
        remainingRegenrationQuota: actionStep?.remainingRegenrationQuota || 0,
      };

      setApiRef(true);

      setRegeneratedStepValue(payload);
      // ** Regenerating the sequence content
      handleRegenerateSequenceContent(payload);

      updateStepContent(stepId, {
        aiContent,
        subject: '',
        body: '',
        isLoading: true,
      });
    },
    [updateStep, steps],
  );

  const handleSaveSequenceList = useCallback(() => {
    const sequenceEmailsList = steps?.map((step, index) => ({
      order: step?.order,
      day: step?.day,
      subject:
        isThreadMode && index !== 0
          ? ''
          : convertHtmlToText(stripChipTagsFromHtml(step?.subject))?.trim(),
      content: convertHtmlToText(stripChipTagsFromHtml(step?.body))?.trim(),
      remainingRegenrationQuota: step?.remainingRegenrationQuota,
    }));

    saveCurrentStepDetails({ emails: sequenceEmailsList });
  }, [saveCurrentStepDetails, steps, isThreadMode]);

  useEffect(() => {
    const stepEmailsList = sequenceList?.emails?.map((email) => ({
      id: email?.order,
      order: email?.order,
      day: email?.day,
      subject: email?.subject || '',
      body: email?.content || '',
      remainingRegenrationQuota: email?.remainingRegenrationQuota || 0,
      aiContent: '',
      isLoading: false,
    }));
    setSteps(stepEmailsList || []);

    const errorStep = sequenceList?.emails?.find((email) => email?.error);
    if (errorStep?.order != null) {
      setPromptErrorStepId(errorStep.order);
    } else {
      setPromptErrorStepId(null);
    }
  }, [sequenceList?.emails]);

  useEffect(() => {
    if (promptErrorStepId !== null) {
      const timeout = setTimeout(() => {
        setPromptErrorStepId(null);
      }, 4000);

      return () => clearTimeout(timeout);
    }

    return () => {};
  }, [promptErrorStepId]);

  useEffect(() => {
    executeOnRequestStatus({
      status: saveAiSeqGeneratorStepDetailsRequestStatus,
      onSuccess: () => {
        setApiRef(false);
      },
      onFailed: () => {
        setApiRef(false);
      },
    });
  }, [saveAiSeqGeneratorStepDetailsRequestStatus]);

  return (
    <StepWrapper
      heading={sequenceList?.name || ''}
      // headingRightContent={
      //   <div className="feedback-btns">
      //     <div className="like-btn">
      //       <Like width={20} height={20} />
      //     </div>
      //     <div className="dislike-btn">
      //       <Dislike width={20} height={20} />
      //     </div>
      //   </div>
      // }
    >
      <div className="email-sequence-editor">
        <div className="sequence-scroll-area">
          {steps?.map((step, index) => (
            <EmailAccordionItem
              key={step?.id}
              id={step?.id}
              title={step?.day}
              subject={step?.subject}
              body={step?.body}
              aiContent={step?.aiContent}
              onSubjectChange={(val) => updateStep(step?.id, 'subject', val)}
              onBodyChange={(val) => updateStep(step?.id, 'body', val)}
              onAiContentChange={(val) =>
                updateStep(step?.id, 'aiContent', val)
              }
              isExpanded={expandedId === step?.id}
              onExpand={() => handleExpand(step?.id)}
              showError={promptErrorStepId === step?.id}
              isLoading={step?.order === regeneratedStep?.order}
              onTriggerAI={(aiContent) => handleTriggerAI(step?.id, aiContent)}
              isDisabled={false}
              remainingRegenrationQuota={step?.remainingRegenrationQuota || 0}
              onDismissError={() => setPromptErrorStepId(null)}
              isSubjectReadOnly={isThreadMode && index !== 0}
            />
          ))}
        </div>
      </div>

      <AiSequenceModalFooter
        disableContinueBtn={isAnyStepInvalid || apiRef}
        onClickContinue={handleSaveSequenceList}
        customClassName="ai-gradient-btn"
        imageSrc={Images.StarsWhite}
        submitlabel="Add to Sequence"
        isLoading={
          !apiRef &&
          getIsRequestPending(saveAiSeqGeneratorStepDetailsRequestStatus)
        }
        showCheckbox
        checkboxLabel="Send follow-up in same thread"
        isCheckboxChecked={isThreadMode}
        onCheckboxChange={setIsThreadMode}
        loadingText="Adding..."
      />
    </StepWrapper>
  );
};

export default memo(SequenceEditStep);
